<template>
  <v-container v-if="!userIsAdmin()" class="fill-height">
    <NoPermission />
  </v-container>
  <div v-if="userIsAdmin()">
    <v-form @submit.prevent="submitForm">
      <v-container>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="user.usuario"
              label="Usuario"
              required
              :rules="[(v) => !!v || 'Usuario es requerido']"
              :disabled="!!user.id"
            ></v-text-field>
          </v-col>
          <v-col md="4">
            <v-text-field
              v-model="user.iniciales"
              label="Iniciales"
              required
              :rules="[
                (v) => !!v || 'Iniciales son requeridas',
                (v) => v.length <= 3 || 'Máximo 3 caracteres'
              ]"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="user.nombre"
              label="Nombre"
              required
              :rules="[(v) => !!v || 'Nombre es requerido']"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="user.permiso"
              :items="SELECT_PERMISOS"
              item-title="text"
              item-value="value"
              label="Permisos"
              multiple
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="user.hotel"
              :items="SELECT_HOTELES"
              item-title="text"
              item-value="value"
              label="Elige el hotel"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="user.puesto"
              :items="SELECT_PUESTOS"
              item-title="text"
              item-value="value"
              label="Puesto"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu :close-on-content-click="false" location="bottom" offset-y>
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="user.color"
                  label="Color del usuario"
                  readonly
                  v-bind="props"
                  prepend-inner-icon="mdi-palette"
                >
                  <template v-slot:append>
                    <div
                      class="color-preview mr-2"
                      :style="{ backgroundColor: user.color || '#FFFFFF' }"
                      style="
                        width: 24px;
                        height: 24px;
                        border-radius: 4px;
                        border: 1px solid #ccc;
                      "
                    ></div>
                  </template>
                </v-text-field>
              </template>
              <v-card min-width="300">
                <v-card-text>
                  <v-color-picker
                    v-model="user.color"
                    show-swatches
                    swatches-max-height="150px"
                  ></v-color-picker>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="text"
                    color="primary"
                    @click="
                      $root.$el
                        .querySelector('.v-menu')
                        .classList.remove('v-overlay--active')
                    "
                  >
                    Cerrar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <!-- <v-col cols="12" md="4">
          <v-switch
            v-model="user.cambiarclave"
            label="Cambiar clave"
            :true-value="'S'"
            :false-value="'N'"
            color="blue"
          ></v-switch>
        </v-col> -->
        </v-row>
        <v-row v-if="!user.id">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="user.passw"
              label="Contraseña"
              type="password"
              required
              :rules="[(v) => !!v || 'Contraseña es requerida']"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" md="6">
            <div v-if="!showPasswordField">
              <v-btn
                color="secondary"
                variant="outlined"
                prepend-icon="mdi-lock-reset"
                @click="showPasswordField = true"
              >
                Cambiar contraseña
              </v-btn>
            </div>
            <v-text-field
              v-else
              v-model="user.passw"
              label="Nueva contraseña"
              type="password"
              :rules="[
                (v) =>
                  v === '' ||
                  v.length >= 6 ||
                  'La contraseña debe tener al menos 6 caracteres'
              ]"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  variant="text"
                  color="error"
                  @click="cancelPasswordChange"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-btn type="submit" color="primary" :loading="loading">
              {{ user.id ? 'Actualizar' : 'Crear' }} Usuario
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, defineComponent } from 'vue'
import { useUsersStore } from '@/stores/usersStore'
import { getToken } from '@/utils/tokenStorage'
import {
  SELECT_PERMISOS,
  SELECT_HOTELES,
  SELECT_PUESTOS
} from '@/utils/constants'
import { defineProps, defineEmits } from 'vue'
import { focusFirstInput, userIsAdmin } from '@/utils/helpers'
import NoPermission from '@/components/common/NoPermission.vue'
defineComponent({
  name: 'UserForm'
})

const props = defineProps({
  id: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['saved'])

const usersStore = useUsersStore()
const loading = ref(false)
const showPasswordField = ref(false)

const user = reactive({
  id: null,
  usuario: '',
  iniciales: '',
  color: '',
  nombre: '',
  permiso: [],
  cambiarclave: 'N',
  hotel: '',
  passw: '',
  deleted: 0,
  puesto: ''
})

onMounted(async () => {
  if (props.id) {
    loading.value = true
    try {
      const userData = await usersStore.getUser(props.id)
      Object.assign(user, userData)
    } catch (error) {
      console.error('Error al cargar el usuario:', error)
    } finally {
      loading.value = false
    }
  }
  // Add focus to first enabled input after form is loaded
  focusFirstInput()
})

const cancelPasswordChange = () => {
  showPasswordField.value = false
  user.passw = ''
}

const submitForm = async () => {
  loading.value = true
  try {
    const token = getToken()
    if (user.id) {
      // eslint-disable-next-line no-unused-vars
      const { usuario, ...updateData } = user
      await usersStore.updateUser(updateData, token)
    } else {
      await usersStore.createUser({ ...user }, token)
    }
    emit('saved')
  } catch (error) {
    console.error('Error al guardar el usuario:', error)
  } finally {
    loading.value = false
  }
}
</script>
